// XP curve and level-up effects, per the game-design memo:
//   XP(n) = 50 * n^1.6
// Each level unlocks something visible. Mix of cosmetic and mechanical.

import { db, type Progress } from '../data/db';

export const xpForLevel = (n: number) => Math.round(50 * Math.pow(n, 1.6));

export function totalXpForLevel(n: number): number {
  let sum = 0;
  for (let i = 1; i < n; i++) sum += xpForLevel(i);
  return sum;
}

// Returns the level you'd be at if you had this much cumulative XP.
export function levelForXp(totalXp: number): { level: number; intoLevel: number; needed: number } {
  let lvl = 1;
  let consumed = 0;
  while (true) {
    const needed = xpForLevel(lvl);
    if (consumed + needed > totalXp) return { level: lvl, intoLevel: totalXp - consumed, needed };
    consumed += needed;
    lvl += 1;
    if (lvl > 200) return { level: lvl, intoLevel: 0, needed: 0 }; // sanity
  }
}

// Each level unlocks something visible. Rotating: hat, spell, pet-skip, hp, wand, …
export interface LevelUnlock {
  kind: 'hat' | 'wand' | 'spell' | 'maxHp' | 'sageCharge';
  id: string;
  label: string;
}

const HATS = ['witch', 'star', 'horns', 'crown', 'flame', 'moon', 'ice', 'leaf'];
const WANDS = ['oak', 'crystal', 'ember', 'frost', 'shadow', 'gold'];

export function unlockForLevel(level: number): LevelUnlock | null {
  // Level 1 has no unlock (you start there).
  if (level <= 1) return null;
  const slot = (level - 2) % 5;
  switch (slot) {
    case 0: return { kind: 'hat', id: HATS[Math.floor((level - 2) / 5) % HATS.length], label: 'New hat!' };
    case 1: return { kind: 'wand', id: WANDS[Math.floor((level - 2) / 5) % WANDS.length], label: 'New wand!' };
    case 2: return {
      kind: 'spell',
      id: level >= 5 ? 'sage-whisper' : 'memory-lock',
      label: level >= 5 ? 'Spell: Sage Whisper' : 'Spell: Memory Lock',
    };
    case 3: return { kind: 'maxHp', id: `hp+${level}`, label: '+1 max HP' };
    case 4: return { kind: 'sageCharge', id: `sage+${level}`, label: '+1 daily Sage Whisper' };
  }
  return null;
}

export async function grantXp(profileId: string, amount: number): Promise<{ leveledUp: boolean; newLevel: number; unlocks: LevelUnlock[] }> {
  const progress = await db.progress.get(profileId);
  if (!progress) return { leveledUp: false, newLevel: 1, unlocks: [] };

  const oldLevel = progress.level;
  progress.xp += amount;
  const newLvl = levelForXp(progress.xp + totalXpForLevel(1)); // total includes all prior
  // Simpler: just walk forward.
  let lvl = progress.level;
  let intoLevel = 0;
  // Recompute level from raw xp.
  // We treat progress.xp as cumulative-across-all-time.
  const computed = levelForXp(progress.xp);
  lvl = computed.level;
  intoLevel = computed.intoLevel;
  progress.level = lvl;

  const unlocks: LevelUnlock[] = [];
  if (lvl > oldLevel) {
    for (let l = oldLevel + 1; l <= lvl; l++) {
      const u = unlockForLevel(l);
      if (!u) continue;
      unlocks.push(u);
      if (u.kind === 'hat') progress.unlockedHats.push(u.id);
      else if (u.kind === 'wand') progress.unlockedWands.push(u.id);
      else if (u.kind === 'spell') {
        if (!progress.unlockedSpells.includes(u.id)) progress.unlockedSpells.push(u.id);
      } else if (u.kind === 'maxHp') {
        progress.maxHp += 1;
        progress.hp = Math.min(progress.maxHp, progress.hp + 1);
      }
    }
  }

  await db.progress.put(progress);
  return { leveledUp: lvl > oldLevel, newLevel: lvl, unlocks };
}

// Reward sizing per outcome.
export const XP_REWARDS = {
  kill: 10,
  crit: 5,             // bonus on top of `kill`
  chestOpened: 20,
  bossDefeated: 50,
};
